import { useAppSelector } from "reducers/store"

import { useCallback, useMemo } from "react"
import { useMutation, useQueryClient } from "react-query"
import ApiProducts from "api/ApiProducts"
import { SET_PRODUCT_LIST } from "reducers/products/actions-types"
import { FIRST_STEPS } from "reducers/accounts/actions-types"
import useHandleApiError from "hooks/useHandleApiErrors"

export type ProductSubmitTypes = "create" | "update" | "updatePublished"

const useProductRequest = (submitType: ProductSubmitTypes, onSuccess: () => void, defaultErrorMessage: string) => {
  const queryClient = useQueryClient()

  const { product } = useAppSelector((state) => state.product)

  const { id } = product

  const {
    identifiers: { productId }
  } = useAppSelector((state) => state.product)

  const successAction = useCallback(() => {
    onSuccess?.()
    queryClient.invalidateQueries(SET_PRODUCT_LIST)
    queryClient.invalidateQueries(FIRST_STEPS)
  }, [onSuccess])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const publishOrUnpublishProduct = useMutation({
    mutationFn: async () => {
      await new ApiProducts().updateIsPublished(id)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const createProduct = useMutation({
    mutationFn: async (data?: App.ProductPayload) => {
      if (!data) return

      return new ApiProducts().createProductWithFiles(data.product, data.filesObj)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const updateProduct = useMutation({
    mutationFn: async (data?: App.ProductPayload) => {
      if (!data) return

      return new ApiProducts().updateProductWithFiles(data.product, data.filesObj, productId)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const isLoading = useMemo(() => {
    return createProduct.isLoading || updateProduct.isLoading
  }, [createProduct.isLoading, updateProduct.isLoading])

  const onSubmit = useCallback(
    (data?: App.ProductPayload) => {
      switch (submitType) {
        case "create":
          createProduct.mutate(data)
          break
        case "update":
          updateProduct.mutate(data)
          break
        case "updatePublished":
          publishOrUnpublishProduct.mutate()
          break
        default:
          break
      }
    },
    [submitType]
  )

  return {
    onSubmit,
    isLoading
  }
}

export default useProductRequest
